import { ReactNode, useEffect, useRef, useState } from 'react';

import { Progress } from '@/components/ui/progress';
import { useGetCaseContextProgressQuery } from '@/services/api/caseContextProgressService';

interface Props {
  matterId: string;
  children: ReactNode;
  className?: string;
  mode?: 'inline' | 'fixed';
}

export const CaseContextGenerationSpinner = ({
  matterId,
  children,
  className,
  mode = 'inline',
}: Props) => {
  const { data } = useGetCaseContextProgressQuery(matterId, {
    pollingInterval: 500,
  });

  // Track last progress update to detect stalls (possible failure)
  const lastDoneRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<number>(Date.now());

  const [isStalled, setIsStalled] = useState(false);

  useEffect(() => {
    if (!data) return;

    if (lastDoneRef.current === null || data.done !== lastDoneRef.current) {
      // Progress advanced
      lastDoneRef.current = data.done;
      lastUpdateRef.current = Date.now();
      setIsStalled(false);
    } else if (Date.now() - lastUpdateRef.current > 30_000) {
      // No progress for 30s -> consider workflow failed/stalled
      setIsStalled(true);
    }
  }, [data]);

  const showProgress =
    !isStalled && data && data.total > 0 && data.done < data.total;

  if (mode === 'inline') {
    return (
      <div
        className={`relative inline-block ${className || ''} ${
          showProgress ? 'pointer-events-none' : ''
        }`}
      >
        <span className={showProgress ? 'invisible' : 'visible'}>
          {children}
        </span>
        {showProgress && (
          <div className="absolute inset-0 z-0 flex items-center justify-center bg-white/70 backdrop-blur-sm rounded">
            <Progress
              value={(data.done / data.total) * 100}
              className="w-24 h-2"
            />
          </div>
        )}
      </div>
    );
  }

  // Non-inline mode: simply render children (no global bar)
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
